import { FullWidthBanner } from '@systemeio/components';
import { Edit03Icon, LogOut01Icon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { updateRuleEmail, updateRuleSpecificEmail } from 'modules/automation-rules/email/api/rule-email-api';
import { RuleSpecificEmailApiInterface } from 'modules/automation-rules/email/types/rule-email-interface';
import { FilterEmailApiInterface } from 'modules/automation-rules/types/automation-rule-filter-interface';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { Breadcrumbs, Button, toast, Tooltip, type BreadcrumbsData } from 'shared/components';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { MailingPoolStatusEnum, MailingPoolStatusHelpMessages } from 'shared/components/dashboard-mail-editor/enums/mailing-pool-status-enum';
import { useEmail } from 'shared/components/dashboard-mail-editor/hooks/use-email';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { useEmailWithRecipient } from 'shared/components/dashboard-mail-editor/hooks/use-email-with-recipient';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorResponseContentInterface, EmailErrorType, EmailId, EmailRecipientErrorResponseContentInterface, EmailRecipientInterface, EmailUpdateInterface, MailingApiInterface, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { EmailWithRecipientApiInterface } from 'shared/components/dashboard-mail-editor/types/email-with-recipient';
import { getEmailStatusLink } from 'shared/components/dashboard-mail-editor/utils/get-email-status-link';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import { Page } from 'shared/components/page';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { CONTENT_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
import { twMerge } from 'tailwind-merge';
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: []
};
interface RuleEmailEditFlowProps {
  withRecipient?: boolean;
  emailId: EmailId;
  mutateEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  mutateSpecificEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  onSetNewEmail?: (data: {
    id: number;
    subject: string;
  }) => void;
}
const RuleEmailEditFlow = ({
  withRecipient,
  emailId,
  mutateEmailData,
  mutateSpecificEmailData,
  onSetNewEmail
}: RuleEmailEditFlowProps) => {
  const {
    t
  } = useLocoTranslation();
  const router = useRouter();
  const [opened, setOpened] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const {
    data: ruleEmail,
    mutate: mutateEmail
  } = useEmail(emailId, !withRecipient && shouldFetch);
  const {
    data: ruleEmailWithRecipient,
    mutate: mutateEmailWithRecipient
  } = useEmailWithRecipient(emailId, !!withRecipient && shouldFetch);
  const {
    data: emailOptions
  } = useEmailOptions(opened);
  const [tempState, setTempState] = useState<EmailRecipientInterface>();
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const setEmail = useCallback((ruleEmail_0: MailingApiInterface | undefined) => {
    if (ruleEmail_0) {
      setTempState(prev => ({
        ...prev,
        body: ruleEmail_0.currentEmail.body,
        subject: ruleEmail_0.currentEmail.subject,
        previewText: ruleEmail_0.currentEmail.previewText,
        fromEmail: ruleEmail_0.currentEmail.fromEmail,
        fromName: ruleEmail_0.currentEmail.fromName,
        recipient: ''
      }));
      setAttachmentsTempState(ruleEmail_0.currentEmail.attachments);
    }
  }, []);
  const setRecipientEmail = useCallback((ruleEmailWithRecipient_0: EmailWithRecipientApiInterface | undefined) => {
    if (ruleEmailWithRecipient_0) {
      setTempState(prev_0 => ({
        ...prev_0,
        recipient: ruleEmailWithRecipient_0.recipient,
        body: ruleEmailWithRecipient_0.currentEmail.body,
        subject: ruleEmailWithRecipient_0.currentEmail.subject,
        previewText: ruleEmailWithRecipient_0.currentEmail.previewText,
        fromEmail: ruleEmailWithRecipient_0.currentEmail.fromEmail,
        fromName: ruleEmailWithRecipient_0.currentEmail.fromName
      }));
      setAttachmentsTempState(ruleEmailWithRecipient_0.currentEmail.attachments);
    }
  }, []);
  useEffect(() => {
    setEmail(ruleEmail);
  }, [ruleEmail, setEmail]);
  useEffect(() => {
    setRecipientEmail(ruleEmailWithRecipient);
  }, [ruleEmailWithRecipient, setRecipientEmail]);
  const onSave = async () => {
    try {
      if (tempState) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_5 => ({
            ...prev_5,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        if (withRecipient && !isValidEmail(tempState.recipient)) {
          return setErrors(prev_6 => ({
            ...prev_6,
            recipient: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const currentEmail_0: EmailUpdateInterface = getMailingUpdateData({
          fromEmail: tempState.fromEmail,
          fromName: tempState.fromName,
          subject: tempState.subject,
          previewText: tempState.previewText,
          body: tempState.body
        }, attachmentsTempState);
        const emailResponse = await (withRecipient ? updateRuleSpecificEmail(emailId, {
          recipientAddress: tempState.recipient,
          mailing: {
            currentEmail: currentEmail_0
          }
        }) : updateRuleEmail(emailId, currentEmail_0));
        if (withRecipient) {
          const response = emailResponse.data as RuleSpecificEmailApiInterface;
          await mutateEmailWithRecipient(data => {
            if (data) {
              return {
                ...data,
                recipient: response.recipientAddress,
                currentEmail: {
                  ...response.mailing.currentEmail,
                  fromEmail: response.mailing.currentEmail.fromEmail,
                  fromName: response.mailing.currentEmail.fromName,
                  subject: response.mailing.currentEmail.subject,
                  previewText: response.mailing.currentEmail.previewText,
                  body: response.mailing.currentEmail.body,
                  attachments: response.mailing.currentEmail.attachments
                }
              };
            }
          }, false);
          if (mutateSpecificEmailData) {
            await mutateSpecificEmailData(data_0 => {
              if (data_0) {
                return data_0.map(el => {
                  if (el.id === response.id) {
                    el = {
                      id: el.id,
                      subject: response.mailing.currentEmail.subject
                    };
                  }
                  return el;
                });
              }
            }, false);
          }
        } else {
          const response_0 = emailResponse.data as MailingApiInterface;
          let newData: EmailBaseInterface;
          if (response_0.currentEmail) {
            newData = response_0.currentEmail;
          } else {
            newData = {
              ...currentEmail_0
            };
          }
          if (mutateEmailData) {
            await mutateEmailData(data_1 => {
              if (data_1) {
                return data_1.map(el_0 => {
                  if (el_0.id === ruleEmail?.currentEmail.id) {
                    el_0 = {
                      id: response_0.currentEmail.id,
                      subject: newData.subject
                    };
                  }
                  return el_0;
                });
              }
            }, false);
          }
          await mutateEmail(data_2 => {
            if (data_2) {
              return {
                ...data_2,
                currentEmail: {
                  id: response_0.currentEmail.id,
                  fromEmail: newData.fromEmail,
                  fromName: newData.fromName,
                  subject: newData.subject,
                  previewText: newData.previewText,
                  body: newData.body,
                  attachments: response_0.currentEmail.attachments
                }
              };
            }
          }, false);
          if (onSetNewEmail) {
            onSetNewEmail({
              id: response_0.currentEmail.id,
              subject: response_0.currentEmail.subject
            });
          }
        }
        toast.success(t('global.changes_saved'));
        setIsFetching(false);
        closeEditor();
        await removeDbRecord();
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (withRecipient) {
          const errors_0 = e.errors as EmailRecipientErrorResponseContentInterface;
          if (errors_0.fields) {
            if (errors_0.fields.recipientAddress) {
              setErrors(prev_1 => ({
                ...prev_1,
                recipient: errors_0.fields.recipientAddress?.join('')
              }));
            }
            const currentEmail = errors_0.fields.mailing?.currentEmail;
            if (currentEmail) {
              setErrors(prev_2 => ({
                ...prev_2,
                body: currentEmail.body?.join(''),
                fromEmail: currentEmail.fromEmail?.join(''),
                fromName: currentEmail.fromName?.join(''),
                subject: currentEmail.subject?.join(''),
                previewText: currentEmail.previewText?.join('')
              }));
            }
          }
        } else {
          const errors_1 = e.errors as EmailErrorResponseContentInterface;
          if (errors_1.fields) {
            const fields = errors_1.fields as unknown as Record<keyof EmailBaseInterface, string[]>;
            setErrors(prev_3 => ({
              ...prev_3,
              body: fields.body?.join(''),
              fromEmail: fields.fromEmail?.join(''),
              fromName: fields.fromName?.join(''),
              subject: fields.subject?.join(''),
              previewText: fields.previewText?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_4 => {
            return {
              ...prev_4,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord,
    setNeedToShowModal
  } = useUnsavedEmail({
    mailingId: withRecipient ? ruleEmailWithRecipient?.id : ruleEmail?.id,
    currentBody: tempState?.body,
    initialBody: withRecipient ? ruleEmailWithRecipient?.currentEmail.body : ruleEmail?.currentEmail.body,
    onPaste: data_3 => setTempState(prev_7 => prev_7 && {
      ...prev_7,
      body: data_3
    })
  });
  const onChangeEmail = (value: EmailRecipientInterface) => setTempState(value);
  const onOpen = () => {
    setOpened(true);
    setShouldFetch(true);
    setNeedToShowModal(true);
  };
  const onAfterLeave = () => {
    setErrors(defaultErrors);
    setShouldFetch(false);
    setEmail(ruleEmail);
    setRecipientEmail(ruleEmailWithRecipient);
  };
  const closeEditor = () => {
    setOpened(false);
    onAfterLeave();
  };
  useEffect(() => {
    if (!opened) return;
    const refreshEmail = async () => {
      await mutateEmail();
      await mutateEmailWithRecipient();
    };
    void refreshEmail();
  }, [mutateEmail, mutateEmailWithRecipient, opened]);
  const deliverabilityMessageKey = emailOptions && (emailOptions.emailPool === MailingPoolStatusEnum.high_risk || emailOptions.emailPool === MailingPoolStatusEnum.quarantine) ? MailingPoolStatusHelpMessages[emailOptions.emailPool] : undefined;
  const breadcrumbsData: BreadcrumbsData[] = [{
    caption: t('dashboard.automation_rule.title_plural'),
    href: '/automation-rules',
    isStatic: true
  }, {
    caption: t(router.pathname.includes('/create') ? 'dashboard.automation_rule.edit.create_title' : 'dashboard.automation_rule.edit.title'),
    isStatic: true
  }, ...(tempState?.subject ? [{
    caption: tempState.subject,
    isStatic: true
  }] : [])];
  return <>
      <Tooltip mode={'hover'} label={t('dashboard.automation_rule.edit.edit_email')}>
        <Button aria-label={t('dashboard.automation_rule.edit.edit_email')} onClick={onOpen} size="icon-sm" variant="ghost-color">
          <Edit03Icon />
        </Button>
      </Tooltip>
      {opened && <div className="bg-bg-page fixed inset-x-0 top-(--dashboard-navbar-height) bottom-0 z-50 overflow-y-auto overscroll-contain [--sticky-top:0px]">
          <Page className={twMerge(CONTENT_CLASSNAME, 'max-tablet:pb-24 tablet:py-3 desktop:py-4 py-4')}>
            <Page.Header className="px-0" actions={[<Button className="w-22.5" type="button" disabled={isFetching} onClick={closeEditor} variant="secondary" size="sm">
                  <LogOut01Icon />
                  {t('global.exit')}
                </Button>, <Tooltip mode="hover" label={t('dashboard.email.warning.update_automation_workflow_email')} disabled={withRecipient ? !ruleEmailWithRecipient : !ruleEmail}>
                  <Button className="w-22.5" type="button" disabled={withRecipient ? !ruleEmailWithRecipient : !ruleEmail} isLoading={isFetching} onClick={onSave} variant="primary" size="sm">
                    {t('global.save')}
                  </Button>
                </Tooltip>]}>
              <Breadcrumbs data={breadcrumbsData} />
            </Page.Header>
            {deliverabilityMessageKey && <Page.Banner>
                <FullWidthBanner variant="warning">
                  <TransLoco t={t} i18nKey={deliverabilityMessageKey} components={{
              a: <a className="link" href={getEmailStatusLink()} target="_blank" rel="noreferrer" />
            }} />
                </FullWidthBanner>
              </Page.Banner>}
            <Page.Body>
              <DashboardMailEditor withRecipient={withRecipient} isPreFetching={withRecipient ? !ruleEmailWithRecipient : !ruleEmail} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} />
            </Page.Body>
          </Page>
        </div>}
      {UnsavedEmailConfirmationModal}
    </>;
};
export default RuleEmailEditFlow;